<template>
    <div :class="$style.list">
        <div :class="$style.tableHeader" class="clearfix">
            <div :class="$style.left">
                <pt-input
                    size="small"
                    theme="underline"
                    clearable
                    v-model="listSearchModel"
                    :placeholder="$t('experience.common.search_nonabtest_list_placeholder')"
                >
                    <template #prefix>
                        <pt-icon
                            icon="pt-icon-search"
                            :style="{
                                width: '24px',
                                height: '24px',
                                fill: '#5E6C84'
                            }"
                        ></pt-icon>
                    </template>
                </pt-input>
            </div>
            <div :class="$style.right">
                <Filter
                    :options="statusFilterOptions"
                    :label="$t('experience.common.filter')"
                    @filter="handleFilterStatus"
                ></Filter>
                <Filter
                    class="pt-ml20"
                    :label="$t('experience.common.type')"
                    :options="typefilterOptions"
                    @filter="handleFilterType"
                ></Filter>
            </div>
        </div>
        <div v-if="filterList && filterList.length > 0" :class="$style.tableContainer">
            <div
                v-for="item in filterList"
                :key="item.id"
                :class="$style.campaignItem"
                @click="handleSelectItem(item)"
            >
                <div :class="$style.thumbnail">
                    <Thumbnail
                        v-if="item.versions?.[0]?.screenshot"
                        :src="item.versions && item.versions[0] && item.versions[0].screenshot"
                        :engageType="item.engageType"
                    >
                    </Thumbnail>
                </div>
                <span :class="$style.name" :title="item.name">{{ item.name }}</span>
                <ItemStatus :status="item.status" :class="$style.status"></ItemStatus>
                <span :class="$style.lastupdate">
                    {{ $t('experience.common.last_update_time') }}
                    {{ dateUtils.formatTimezone(item.updateTime, profileInfo?.timeZone) }}
                </span>
                <div :class="$style.arrow">
                    <span>
                        <pt-icon icon="icon-arrow-right"></pt-icon>
                    </span>
                </div>
            </div>
        </div>
        <div :class="$style.emptyList" v-else>
            <p>{{ $t('experience.report.no_data') }}</p>
        </div>
        <div :class="$style.pagination">
            <pt-pagination
                :prev-text="$t('landing_page_list.prev')"
                :next-text="$t('landing_page_list.next')"
                v-model:currentPage="paginationCurrentPage"
                v-model:pageSize="paginationCurrentPageSize"
                :page-sizes="paginationPageSizeOptions"
                layout="sizes, prev, pager, next"
                :total="totalFilterList && totalFilterList.length"
            >
            </pt-pagination>
        </div>
    </div>
</template>

<script lang="ts">
import Filter from '@experience/components/Filter.vue';
import ItemStatus from '@components/item-status/ItemStatus.vue';
import Thumbnail from '@experience/components/Thumbnail.vue';
export default {
    name: 'ExperienceNonABTestList',
    components: {
        Filter,
        ItemStatus,
        Thumbnail
    }
};
</script>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import dateUtils from '@utils/date.utils';
import {
    experienceStatusList,
    experienceTypeList
} from '@experience/static/config/experience.config';
import { Experience } from '@models/experience/experience';
import { usePermission } from '@models/profile/useProfilePermission';
import analyticsService from '@assets/services/analytics.service';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import type { TypeItem } from '@experience/static/config/experience.type';
import { EntryPosition, ComponentData } from '@experience/store/experience.create.store';

interface Props {
    entryItem: TypeItem;
    entryPosition: EntryPosition;
    data: ComponentData;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);
const router = useRouter();
const { profileInfo } = useProfileInfo();
const { hasExperienceAdvancePermission } = usePermission();

const listSearchModel = ref('');
const filterList = ref<any>([]);
const totalFilterList = ref<any>([]);
const typefilterOptions = [
    {
        key: 'ALL',
        name: 'experience.common.all_experience'
    },
    ...experienceTypeList.filter(item => !['ABTEST', 'REDIRECT'].includes(item.key))
].filter(item => {
    return item.key !== 'ADVANCED' || hasExperienceAdvancePermission.value;
});
const paginationCurrentPage = ref(1);
const paginationPageSizeOptions = [5, 10];
const paginationCurrentPageSize = ref(paginationPageSizeOptions[0]);
const statusFilterOptions = [
    {
        key: 'ALL',
        name: 'experience.common.state_all'
    },
    ...experienceStatusList
];
const activeStatusFilter = ref<TypeItem>(statusFilterOptions[0] as TypeItem);
const activeTypeFilter = ref<TypeItem>(typefilterOptions[0] as TypeItem);
const pagination = computed(() => {
    const startIndex = (paginationCurrentPage.value - 1) * paginationCurrentPageSize.value;
    return {
        startIndex,
        endIndex: startIndex + paginationCurrentPageSize.value
    };
});
watch(
    () => [
        activeStatusFilter.value,
        listSearchModel.value,
        activeTypeFilter.value,
        props.data.experienceNonABTestList,
        pagination.value
    ],
    () => {
        if (!props.data.experienceNonABTestList) return;
        const searchValue = listSearchModel.value?.trim()?.toLocaleLowerCase() || '';
        const status = activeStatusFilter.value?.key?.toLocaleUpperCase();
        const type: any = activeTypeFilter.value?.key;
        const { startIndex, endIndex } = pagination.value;
        totalFilterList.value = props.data.experienceNonABTestList?.filter((item: any) => {
            return (
                (!searchValue ||
                    (searchValue && item.name.toLocaleLowerCase().indexOf(searchValue) !== -1)) &&
                (status === 'ALL' || item.status === status) &&
                (type === 'ALL' || item.engageType === type)
            );
        });
        filterList.value = totalFilterList.value.slice(
            Math.max(0, startIndex),
            Math.min(endIndex, totalFilterList.value?.length)
        );
    },
    {
        immediate: true
    }
);
function handleFilterStatus(item: TypeItem) {
    activeStatusFilter.value = item;
}
function handleFilterType(item: TypeItem) {
    activeTypeFilter.value = item;
}
function handleSelectItem(experience: Experience) {
    const params: any = {
        experienceId: experience.id
    };
    if (!experience.versions || experience.versions.length <= 0) {
        params.type = experience.engageType;
    }
    if (!experience.enableABTest) {
        params.needEnableABTest = true;
    }
    analyticsService.usePTX('cp_add_select_ab_exist_select_cp').track();
    emit('close', {
        name: 'ExperienceSetting',
        params: params
    });
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.list {
    // min-height: 300px;
    .tableHeader {
        margin-bottom: 20px;
        .left {
            width: 256px;
            float: left;
        }
        .right {
            float: right;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
    }
    .tableContainer {
        min-height: 336px;
    }
    .campaignItem {
        padding: 12px;
        border: 1px solid #dfe1e6;
        border-bottom: 1px solid transparent;
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 13px;
        position: relative;
        &:hover {
            border-color: #00a92e;
            border-bottom: 1px solid #00a92e;
            &:nth-last-of-type(1) {
                border-color: #00a92e;
            }
            .arrow span {
                display: block;
            }
        }
        &:nth-of-type(1) {
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
        }
        &:nth-last-of-type(1) {
            border-bottom-left-radius: 3px;
            border-bottom-right-radius: 3px;
            border-bottom: 1px solid #dfe1e6;
        }
        .thumbnail {
            width: 78px;
            height: 42px;
            overflow: hidden;
            border: 1px solid #dfe1e6;
            border-radius: 3px;
            background-color: #fafbfc;
            img {
                width: 100%;
            }
        }
        .name {
            flex: 2;
            font-size: 14px;
            font-weight: 500;
            margin-left: 30px;
            @extend %text-ellipsis;
        }
        .status {
            flex: 1;
            margin-left: 40px;
        }
        .lastupdate {
            flex: 2;
        }
        // .views {
        //     flex: 1;
        // }
        .arrow {
            position: absolute;
            right: 24px;
            span {
                display: block;
                width: 28px;
                height: 28px;
                background-color: #00a92e;
                border-radius: 50%;
                padding: 3px 6px;
                float: right;
                display: none;
                svg {
                    width: 16px;
                    height: 16px;
                    fill: #ffffff;
                    vertical-align: middle;
                }
            }
        }
    }
    .pagination {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }
}
.emptyList {
    min-height: 336px;
    color: #8993a4;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
